import { GroupMemberRole, useAppStore } from '@halo/chat-sdk-react';
import { useNavigate } from 'react-router';
import { get, includes } from 'lodash';
import { tools } from '@halo/ui-chat-react';
import { StateEnum, GroupStatusEnum } from '@halo/chat-sdk';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { useActionsStore } from '@/store/actions';
import { useContactsStore } from '@/store/contacts';
import Paths from '@/configs/router/paths';
import { SyncBackupType, useSettingsStore } from '@/store/settings';
import { useStaticAppStore, isSessionCategory, SessionCategoryPrefix } from '@/store/client';
import { useMediaError } from '@/scripts/hooks';
import { GroupCreateDialog } from '../GroupCreateDialog';
import { GroupInviteDialog } from '../GroupInviteDialog';
import { GroupAddAdminDialog } from '../GroupAddAdminDialog';
import { DeleteChatDialog } from '../DeleteChatDialog';
import { ClearChatHistoryDialog } from '../ClearChatHistoryDialog';
import { DeleteMessagesDialog } from '../DeleteMessagesDialog';
import { ContactsAddDialog } from '../ContactsAddDialog';
import type { SetMyContactDialogForm } from '../MyContactSettingDialog';
import { MyContactSettingDialog } from '../MyContactSettingDialog';
import { GroupRemoveMembersDialog } from '../GroupRemoveMembersDialog';
import { QrCodeDialog } from '../QrCodeDialog';
import { GroupCreateCallDialog } from '../GroupCreateCallDialog';
import { ProfileDialog } from '../ProfileDialog';
import { Transfer } from '../Transfer';
import { LuckyDialog } from '../LuckyDialog';
import { LuckyDetailsDialog } from '../LuckyDialog/LuckyDetailsDialog';
import { ForwardToDialog } from '../ForwardToDialog';
import { GroupJoinRequestsDialog } from '../GroupJoinRequestsDialog';
import { GroupDialog } from '../GroupDialog';
import { RobotProfileDialog } from '../RobotProfileDialog';
import { UserOnBoarding } from '../UserOnboarding';
import { SafetyToolsDialog } from '../SafetyToolsDialog';
// import { ParticipantsDialog } from '../ParticipantsDialog';

export function MainDialog() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { client, mediaCall, hideSession, createSingleSession, createGroupSession } =
    useStaticAppStore();
  const { pinnedSessions, changePinnedList, activatedSessionId, sessionStores } = useAppStore(
    useShallowEnhance([
      'pinnedSessions',
      'changePinnedList',
      'activatedSessionId',
      'sessionStores',
    ])
  );

  const { syncBackupContactChecked, syncBackupCloudData } = useSettingsStore();
  const {
    myContactSettingIsOpen,
    setMyContactSettingIsOpen,
    myContactSettingUser,
    needOpenContactsAddIsOpen,
    myContactSettingOnlyEdit,
    setNeedOpenContactsAddIsOpen,
    contactsAddIsOpen,
    setContactsAddIsOpen,
    contactsAddReset,
    setContactsAddReset,
    groupCreateIsOpen,
    setGroupCreateIsOpen,
    getInitialGroupCreateSelectedUsers,
    groupInviteIsOpen,
    setGroupInviteIsOpen,
    groupInviteData,
    deleteChatSession,
    deleteChatIsOpen,
    setDeleteChatIsOpen,
    deleteChatHistory,
    setDeleteChatHistory,
    deleteMessages,
    deleteMessagesSession,
    setDeleteMessagesIsOpen,
    deleteMessagesIsOpen,
    deleteOtherMessagesAllow,
    deleteOtherMessages,
    setDeleteOtherMessages,
    deleteMessagesOnDelete,
    groupRemoveMembersIsOpen,
    setGroupRemoveMembersIsOpen,
    groupRemoveMembersData,
    qrCodeIsOpen,
    setQrCodeIsOpen,
    qrCodeData,
    setGroupCreateCallIsOpen,
    groupCreateCallIsOpen,
    groupCreateCallData,
    luckyDialogIsOpen,
    luckyDetailsDialogIsOpen,
    luckyDetailsDialogData,
    setLuckyDialogIsOpen,
    setLuckyDetailsDialogIsOpen,
    groupJoinRequestsIsOpen,
    setGroupJoinRequestsIsOpen,
    groupAddAdminIsOpen,
    setGroupAddAdminIsOpen,
    groupAddAdminData,
    clearChatHistoryIsOpen,
    setClearChatHistoryIsOpen,
    clearChatHistoryData,
    safetyToolsIsOpen,
    setSafetyToolsIsOpen,
  } = useActionsStore(
    useShallowEnhance([
      'myContactSettingIsOpen',
      'setMyContactSettingIsOpen',
      'myContactSettingUser',
      'needOpenContactsAddIsOpen',
      'myContactSettingOnlyEdit',
      'setNeedOpenContactsAddIsOpen',
      'contactsAddIsOpen',
      'setContactsAddIsOpen',
      'contactsAddReset',
      'setContactsAddReset',
      'groupCreateIsOpen',
      'setGroupCreateIsOpen',
      'getInitialGroupCreateSelectedUsers',
      'groupInviteIsOpen',
      'setGroupInviteIsOpen',
      'groupInviteData',
      'deleteChatSession',
      'deleteChatIsOpen',
      'setDeleteChatIsOpen',
      'deleteChatHistory',
      'setDeleteChatHistory',
      'deleteMessages',
      'deleteMessagesSession',
      'setDeleteMessagesIsOpen',
      'deleteMessagesIsOpen',
      'deleteOtherMessagesAllow',
      'deleteOtherMessages',
      'setDeleteOtherMessages',
      'deleteMessagesOnDelete',
      'groupRemoveMembersIsOpen',
      'setGroupRemoveMembersIsOpen',
      'groupRemoveMembersData',
      'qrCodeIsOpen',
      'setQrCodeIsOpen',
      'qrCodeData',
      'setGroupCreateCallIsOpen',
      'groupCreateCallIsOpen',
      'groupCreateCallData',
      'luckyDialogIsOpen',
      'luckyDetailsDialogIsOpen',
      'luckyDetailsDialogData',
      'setLuckyDialogIsOpen',
      'setLuckyDetailsDialogIsOpen',
      'groupJoinRequestsIsOpen',
      'setGroupJoinRequestsIsOpen',
      'groupAddAdminIsOpen',
      'setGroupAddAdminIsOpen',
      'groupAddAdminData',
      'clearChatHistoryIsOpen',
      'setClearChatHistoryIsOpen',
      'clearChatHistoryData',
      'safetyToolsIsOpen',
      'setSafetyToolsIsOpen',
    ])
  );
  const sessionStoreInstance = useMemo(() => {
    return activatedSessionId ? sessionStores[activatedSessionId] : null;
  }, [activatedSessionId, sessionStores]);

  const { mediaError } = useMediaError();

  const { getContacts } = useContactsStore();

  const [loading, setLoading] = useState(false);

  const handleOkContact = useCallback(
    async ({ remarkFirstName, remarkLastName }: SetMyContactDialogForm) => {
      try {
        setLoading(true);
        if (!myContactSettingOnlyEdit) {
          await createSingleSession(myContactSettingUser?.user_id ?? '');
        }
        await client?.Api.contacts.setRemarkName(
          myContactSettingUser?.user_id ?? '',
          remarkFirstName,
          remarkLastName
        );
        if (!myContactSettingOnlyEdit) {
          await client?.Api.contacts.contactsAdd(myContactSettingUser?.user_id ?? '');
        }
        syncBackupCloudData({ type: SyncBackupType.CONTACTS }).catch(() => {});
        setMyContactSettingIsOpen(false);
        setContactsAddReset(true);
        setNeedOpenContactsAddIsOpen(false);
        getContacts(client!);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [
      client,
      createSingleSession,
      getContacts,
      myContactSettingOnlyEdit,
      myContactSettingUser?.user_id,
      setContactsAddReset,
      setMyContactSettingIsOpen,
      setNeedOpenContactsAddIsOpen,
      syncBackupCloudData,
    ]
  );

  const handleDeleteContact = useCallback(async () => {
    try {
      setLoading(true);
      await client?.Api.contacts.contactsDelete(myContactSettingUser?.user_id ?? '');
      syncBackupCloudData({ type: SyncBackupType.CONTACTS }).catch(() => {});
      setMyContactSettingIsOpen(false);
      getContacts(client!);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [
    client,
    getContacts,
    myContactSettingUser?.user_id,
    setMyContactSettingIsOpen,
    syncBackupCloudData,
  ]);

  return (
    <>
      <ContactsAddDialog
        reset={contactsAddReset}
        isOpen={contactsAddIsOpen}
        setIsOpen={setContactsAddIsOpen}
        onClose={() => {
          setContactsAddReset(true);
          setContactsAddIsOpen(false);
          setNeedOpenContactsAddIsOpen(false);
        }}
        onAddContact={async (user) => {
          setContactsAddReset(false);
          setContactsAddIsOpen(false);
          setMyContactSettingIsOpen(true, user);
          setNeedOpenContactsAddIsOpen(true);
        }}
      />
      <MyContactSettingDialog
        loading={loading}
        isOpen={myContactSettingIsOpen}
        setIsOpen={setMyContactSettingIsOpen}
        myContactSettingOnlyEdit={myContactSettingOnlyEdit}
        user={myContactSettingUser}
        syncBackupContactChecked={syncBackupContactChecked}
        onClose={() => {
          setMyContactSettingIsOpen(false);
          if (needOpenContactsAddIsOpen) {
            setContactsAddIsOpen(true);
          }
        }}
        onOK={handleOkContact}
        onDeleteContact={handleDeleteContact}
      />

      <GroupCreateDialog
        isOpen={groupCreateIsOpen}
        setIsOpen={setGroupCreateIsOpen}
        sessionStoreInstance={sessionStoreInstance}
        onClose={() => setGroupCreateIsOpen(false)}
        getInitialGroupCreateSelectedUsers={getInitialGroupCreateSelectedUsers}
        onOK={async (name, selected, forwardData, avatar) => {
          try {
            let avatarSrc: string | undefined = '';
            if (avatar) {
              avatarSrc = await client?.Api.session.uploadAvatar({ file: avatar });
            }
            const session = await createGroupSession(
              name,
              avatarSrc || '',
              '',
              selected.filter((item) => !item.isGroup).map((item) => item.user_id),
              selected.filter((item) => item.isGroup).map((item) => item.user_id)
            );
            tools.toaster.success({
              title: t('tips.groupCreatedSuccessfully'),
            });
            if (forwardData) {
              const { forwardMsg, refComponent } = forwardData;
              if (forwardMsg) {
                await refComponent?.current?.send({
                  sessionIds: [session?.chat_id],
                  forwardMsg,
                });
              }
            }
            navigate(Paths.MESSAGES_CHATS + '/' + session?.chat_id);
            setGroupCreateIsOpen(false);
          } catch (error) {
            console.error('Failed to create group', error);
            tools.toaster.error({
              title: t('tips.failedToCreateGroup'),
            });
          }
        }}
      />

      <GroupInviteDialog
        sessionStoreInstance={sessionStoreInstance}
        checkUserKey={true}
        isOpen={groupInviteIsOpen}
        setIsOpen={setGroupInviteIsOpen}
        onClose={() => setGroupInviteIsOpen(false)}
        onOK={async (selected, groupInviteDialogRef) => {
          const canInviteMember = sessionStoreInstance?.getState()?.groupAccess?.canInviteMember;
          if (!canInviteMember) {
            try {
              const sessionIds = await Promise.all(
                selected.map((item) => createSingleSession(item.user_id))
              );
              sessionIds.forEach((item) => {
                groupInviteDialogRef?.current?.send?.(
                  item?.chat_id ?? '',
                  groupInviteData?.session?.name ?? '',
                  groupInviteData?.groupInfo?.share_url ?? ''
                );
              });
              tools.toaster.success({
                title: t('tips.inviteLinkSent'),
              });
              setGroupInviteIsOpen(false);
            } catch (error) {
              console.error('Failed to invite members', error);
              tools.toaster.error({
                title: t('tips.failedToInviteMembers'),
              });
            }
          } else {
            try {
              await client?.Api.session.inviteGroupMembers(
                groupInviteData?.session?.chat_id ?? '',
                selected.map((item) => item.user_id)
              );
              setGroupInviteIsOpen(false);
            } catch {
              tools.toaster.error({
                title: t('tips.failedToInviteMembers'),
              });
            }
          }
        }}
      />

      <DeleteChatDialog
        okLoading={loading}
        open={deleteChatIsOpen}
        onClose={() => setDeleteChatIsOpen(false)}
        avatar={deleteChatSession?.avatar ?? ''}
        chatName={deleteChatSession?.name ?? ''}
        deleteChatHistory={deleteChatHistory}
        setDeleteChatHistory={setDeleteChatHistory}
        onOk={async () => {
          setLoading(true);
          await hideSession(deleteChatSession!.chat_id, deleteChatHistory);
          setLoading(false);
          navigate(Paths.MESSAGES_CHATS);
          setDeleteChatIsOpen(false);
          setDeleteChatHistory(true);
          if (deleteChatSession?.chat_id && get(deleteChatSession, 'isGroupError')) {
            changePinnedList(deleteChatSession?.chat_id, false, false);
          }
          const isGroup = isSessionCategory(
            deleteChatSession?.chat_id,
            SessionCategoryPrefix.Group
          );
          const include = includes(pinnedSessions, deleteChatSession?.chat_id);
          const isDeletedAccount =
            !isGroup && deleteChatSession?.user_info?.state === StateEnum.DELETE_ACCOUNT;
          let isGroupDisband = false;
          if (isGroup && deleteChatSession?.chat_id && include) {
            const result = await client?.Api.session.getGroupInfo(deleteChatSession?.chat_id);
            if (result?.status === GroupStatusEnum.DISBAND) {
              isGroupDisband = true;
            }
          }
          if ((isDeletedAccount || isGroupDisband) && include && deleteChatSession?.chat_id) {
            changePinnedList(deleteChatSession?.chat_id, false, false);
          }
        }}
      />
      <ClearChatHistoryDialog
        open={clearChatHistoryIsOpen}
        onClose={() => setClearChatHistoryIsOpen(false)}
        onOk={async () => {
          await sessionStoreInstance?.getState().clearMessages(clearChatHistoryData?.chat_id);
          setClearChatHistoryIsOpen(false);
        }}
      />
      <DeleteMessagesDialog
        open={deleteMessagesIsOpen}
        onClose={() => setDeleteMessagesIsOpen(false)}
        deleteOtherMessagesAllow={deleteOtherMessagesAllow}
        deleteOtherMessages={deleteOtherMessages}
        setDeleteOtherMessages={setDeleteOtherMessages}
        deleteMessagesSession={deleteMessagesSession}
        deleteMessages={deleteMessages}
        onOk={async () => {
          setDeleteMessagesIsOpen(false);
          if (deleteMessagesSession && deleteMessages.length > 0) {
            await deleteMessagesOnDelete?.();
            await useAppStore
              .getState()
              .sessionStores[deleteMessagesSession?.chat_id]?.getState()
              ?.deleteMessages(
                deleteMessages.map((message) => message.cli_msg_id),
                deleteOtherMessages
              );
          }
        }}
      />
      <GroupRemoveMembersDialog
        title={groupRemoveMembersData?.title}
        list={groupRemoveMembersData?.list || []}
        checkUserKey={false}
        isOpen={groupRemoveMembersIsOpen}
        setIsOpen={setGroupRemoveMembersIsOpen}
        onOK={async (selected) => {
          try {
            await client?.Api.session.removeGroupMembers(
              groupRemoveMembersData?.session?.chat_id ?? '',
              selected.map((item) => item.user_id)
            );
            setGroupRemoveMembersIsOpen(false);
          } catch (_) {
            tools.toaster.error({
              title: t('tips.failedToRemoveMembers'),
            });
          }
        }}
      />
      <ProfileDialog />
      <GroupDialog />
      <RobotProfileDialog />
      <UserOnBoarding />
      <QrCodeDialog open={qrCodeIsOpen} setIsOpen={setQrCodeIsOpen} data={qrCodeData} />
      {!!groupCreateCallData?.list.length && (
        <GroupCreateCallDialog
          checkUserKey={false}
          title={groupCreateCallData?.title}
          list={groupCreateCallData.list}
          isOpen={groupCreateCallIsOpen}
          setIsOpen={setGroupCreateCallIsOpen}
          onOK={async (selected) => {
            if (groupCreateCallData?.session) {
              mediaCall({
                session_id: groupCreateCallData.session!.chat_id,
                type: groupCreateCallData!.type!,
                membersId: selected.map((item) => item.user_id),
              }).catch((error) => {
                mediaError(error, 'call');
              });
            }
            setGroupCreateCallIsOpen(false);
          }}
        />
      )}

      {/* <ParticipantsDialog isOpen={false} setIsOpen={() => {}} /> */}
      {sessionStoreInstance && (
        <LuckyDialog
          isOpen={luckyDialogIsOpen}
          setIsOpen={setLuckyDialogIsOpen}
          sessionStoreInstance={sessionStoreInstance}
        />
      )}

      {sessionStoreInstance && (
        <LuckyDetailsDialog
          isOpen={luckyDetailsDialogIsOpen}
          sessionStoreInstance={sessionStoreInstance}
          luckyDetailsDialogData={luckyDetailsDialogData}
          setIsOpen={setLuckyDetailsDialogIsOpen}
        />
      )}

      {sessionStoreInstance && <Transfer sessionStoreInstance={sessionStoreInstance}></Transfer>}

      {sessionStoreInstance && <ForwardToDialog sessionStoreInstance={sessionStoreInstance} />}
      <GroupAddAdminDialog
        checkUserKey={true}
        isOpen={groupAddAdminIsOpen}
        setIsOpen={setGroupAddAdminIsOpen}
        exceptionProps={groupAddAdminData?.exceptionProps}
        members={groupAddAdminData?.members ?? []}
        onClose={() => setGroupAddAdminIsOpen(false)}
        onOK={async (selected) => {
          try {
            const maxAdminCount = groupAddAdminData?.maxAdminCount ?? Infinity;
            if (selected.length + groupAddAdminData?.adminMembers?.length > maxAdminCount) {
              tools.toaster.error({
                title: t('tips.adminCountExceedsMaximumLimit'),
              });
              return;
            }
            const result = await client?.Api.session.updateGroupMemberRole(
              groupAddAdminData?.session?.chat_id ?? '',
              selected.map((item) => item.user_id),
              GroupMemberRole.ADMIN
            );
            if (result?.update_fail_list?.length) {
              tools.toaster.error({
                title: t('tips.failedToAddAdmin', { count: result?.update_fail_list?.length }),
              });
            } else {
              setGroupAddAdminIsOpen(false);
              tools.toaster.success({
                title: t('tips.addedToAdminSuccessfully', { count: selected.length }),
              });
            }
          } catch (error) {
            console.error('addGroupAdmin', error);
          }
        }}
      />
      {sessionStoreInstance && (
        <GroupJoinRequestsDialog
          isOpen={groupJoinRequestsIsOpen}
          setIsOpen={setGroupJoinRequestsIsOpen}
          sessionStoreInstance={sessionStoreInstance}
        />
      )}
      <SafetyToolsDialog isOpen={safetyToolsIsOpen} setIsOpen={setSafetyToolsIsOpen} />
    </>
  );
}
